import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Item } from '@/stores/canvasStore';
import { usePartnrConfirmStore } from '@/stores/partnrConfirmStore';

/** What the confirm lists for the item: its title, text or file name, else its address. */
export function discoverItemLabel(item: Item): string {
  const c = (item.content ?? {}) as Record<string, unknown>;
  for (const value of [c.title, c.text, c.filename, c.alt, c.url]) {
    if (typeof value === 'string' && value.trim()) return value.trim().slice(0, 120);
  }
  return 'Untitled item';
}

/**
 * Deletes one Discover item for good, after asking: the row, then its
 * uploaded file or video in storage. Shared by the card's trash button, its
 * context menu and the preview, so all three behave the same. Asks in the
 * app's own dialog (mounted by WallView), not window.confirm, which a browser
 * can be told to suppress, silently answering no. True when deleted.
 */
export async function deleteDiscoverItem(item: Item): Promise<boolean> {
  const confirmed = await usePartnrConfirmStore.getState().ask({
    title: 'Delete this item?',
    description: "It is removed from its board and from Discover. This can't be undone.",
    items: [discoverItemLabel(item)],
    confirmLabel: 'Delete',
  });
  if (!confirmed) return false;

  try {
    const { error } = await supabase.from('items').delete().eq('id', item.id);
    if (error) {
      toast.error('Failed to delete');
      return false;
    }

    // The row goes first: if a file removal fails, the item is still gone, not half deleted
    const content = (item.content ?? {}) as Record<string, unknown>;
    for (const url of [content.url, content.videoUrl]) {
      if (typeof url === 'string' && url.includes('supabase')) {
        const filename = url.split('/').slice(-2).join('/'); // user_id/filename
        await supabase.storage.from('uploads').remove([filename]);
      }
    }

    toast.success('Deleted');
    return true;
  } catch (err) {
    console.error('Delete error:', err);
    toast.error('Failed to delete');
    return false;
  }
}
